import React, { Fragment, useCallback } from "react";
import { ExtendedTabId } from "components/ExtendedTabs/ExtendedTabs.types";
import * as S from "./PageSwitcher.styles";
import { useTranslation } from "hooks/useTranslation";
import { useHistory } from "react-router-dom";
import { useSiteSettings } from "context/SiteSettingsContext";
import { absoluteAssetUrl, getOptimizedImageUrl } from "util/assets";
import { getPath } from "util/route";
import { MicrositeHeading } from "pages/LayoutComponents/MicrositeHeading";
import { HOW_IT_WORKS_TABS_CONFIG } from "../../shared.config";

export const PageSwitcher = ({
  activeTabId,
  isOnlyDeploymentTypes,
}: Readonly<{
  activeTabId: ExtendedTabId;
  isOnlyDeploymentTypes?: boolean;
}>) => {
  const { t } = useTranslation();
  const history = useHistory();
  const { routes } = useSiteSettings();

  const handleTabClick = useCallback(
    (tab) => {
      history.replace({
        pathname: getPath(routes, tab.href),
      });
    },
    [history, routes],
  );

  if (isOnlyDeploymentTypes) {
    return (
      <S.DeploymentContainer>
        <S.ScrollWrapper
          direction="horizontal"
          align="center"
          scrollStepType="itemWidth"
          fillType="gradient"
          fillColor="#fff"
          arrowColor="#828893"
          draggable
          items={HOW_IT_WORKS_TABS_CONFIG.map((tab) => ({
            id: tab.id,
            render: () => (
              <S.DeploymentTypeTab
                key={tab.id}
                onClick={() => handleTabClick(tab)}
              >
                <S.DeploymentLabel $isActive={activeTabId === tab.id}>
                  {t(tab.label)}
                  {activeTabId === tab.id ? ` ${t("commons.deployment")}` : ""}
                </S.DeploymentLabel>
              </S.DeploymentTypeTab>
            ),
          }))}
        />
      </S.DeploymentContainer>
    );
  }

  return (
    <Fragment>
      <MicrositeHeading
        title={t("gettingStartedWithUniFi.tabs.title")}
        isCentered
        spacing={{ top: 0, bottom: 32 }}
      />
      <S.TabWrapper>
        {HOW_IT_WORKS_TABS_CONFIG.map((tab, index) => (
          <S.Tab
            key={index}
            $isActive={activeTabId === tab.id}
            onClick={() => handleTabClick(tab)}
          >
            <S.ImageWrapper>
              <S.TabImage
                loading="lazy"
                decoding="async"
                src={getOptimizedImageUrl(absoluteAssetUrl(tab.image), 256)}
                srcSet={`${getOptimizedImageUrl(absoluteAssetUrl(tab.image), 512)} 2x`}
                alt={t(tab.label)}
              />
            </S.ImageWrapper>

            <S.Label>{t(tab.label)}</S.Label>
          </S.Tab>
        ))}
      </S.TabWrapper>
    </Fragment>
  );
};
